import { ProgressBar, Typography } from "@/components";
import { CaseReferralFormFieldSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
} from "@tanstack/react-router";
import { Suspense, lazy, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

const ReferralInfo = lazy(() => import("./-components/Info"));

export const Route = createFileRoute("/dashboard/case/referrals")({
	component: RouteComponent,
});

const WIZARD_PAGES = [
	"/dashboard/case/referrals",
	"/dashboard/case/referrals/type",
	"/dashboard/case/referrals/contact",
	"/dashboard/case/referrals/message",
] as const;

const WIZARD_TITLES = {
	0: "Select case",
	1: "Select type",
	2: "Select contact",
	3: "Message",
} as const;

function RouteComponent() {
	const pathName = useLocation({
		select: (s) => s.pathname,
	});
	const hideHeader = pathName.endsWith("success");
	const currentStep = useMemo(
		() => WIZARD_PAGES.findIndex((step) => step.includes(pathName)) || 0,
		[pathName],
	);
	const formMethod = useForm<CaseReferralFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(CaseReferralFormFieldSchema),
		defaultValues: {
			caseId: 0,
		},
	});

	const tags = formMethod.watch("tags");
	const team = formMethod.watch("team");
	const member = formMethod.watch("member");
	const caseItem = formMethod.watch("_case");

	return (
		<div
			id="referral-wizard"
			className="bg-white flex-1 flex flex-col gap-4 md:gap-8 overflow-auto"
		>
			{!hideHeader ? (
				<div className="container flex flex-col">
					<div className="pt-4 flex justify-start items-center gap-2">
						<Link to="/dashboard">
							<IconButton>
								<CloseRoundedIcon />
							</IconButton>
						</Link>
						<Typography fontWeight={700} fontSize={16}>
							Invite External Member
						</Typography>
					</div>
					<div className="flex flex-col gap-1 justify-center">
						<ProgressBar
							min={0}
							max={4}
							value={currentStep + 1}
							step={currentStep + 1}
						/>
						<Typography fontWeight={700} fontSize={10}>
							Step {currentStep + 1} of 4
						</Typography>
						<Typography fontWeight={400} fontSize={12}>
							{WIZARD_TITLES[currentStep as keyof typeof WIZARD_TITLES]}
						</Typography>
					</div>
				</div>
			) : null}
			<FormProvider {...formMethod}>
				{currentStep > 0 ? (
					<div className="container">
						<Suspense>
							<ReferralInfo
								currentStep={currentStep}
								tags={tags}
								case={caseItem}
								member={member}
								team={team}
							/>
						</Suspense>
					</div>
				) : null}
				<Outlet />
			</FormProvider>
		</div>
	);
}
